import {useQuery} from '@apollo/client'
import {AutoSave} from '@flinkit/ra-form-layout'
import {ReferenceManyToManyField} from '@flinkit/ra-relationships'
import {Typography} from '@mui/material'
import {makeStyles} from '@mui/styles'
import gql from 'graphql-tag'
import {FC} from 'react'
import {
  Datagrid,
  FunctionField,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  useTranslate,
} from 'react-admin'

import ExtendedEdit from '../components/ExtendedEdit'
import useHasPermission from '../hooks/useHasPermission'
import {QueryRoot} from '../types/graphqlSchema'

const ChannelEdit: FC<ChannelProps> = ({channelId}) => {
  const styles = useStyles()
  const {data: {channels_by_pk: channel} = {}} = useQuery<QueryRoot['channels_by_pk']>(
    CHANNEL_WHATSAPP_NUMBER_QUERY, {skip: !channelId, variables: {channelId}}
  )
  return (
    <ExtendedEdit
      className={styles.editRoot}
      id={channelId}
      redirect={false}
      resource="channels"
    >
      <SimpleForm
        resetOptions={{keepDirtyValues: true}}
        toolbar={(
          <AutoSave
            confirmationDuration={false}
            debounce={1000}
            typographyProps={{display: 'none'}}
          />
        )}
      >
        <Typography variant="subtitle2">
          {useTranslate()('resources.channels.edit.inputs.name.subtitle')}
        </Typography>
        <TextInput
          label={
            <Typography variant="body1">
              {useTranslate()(
                'resources.channels.edit.inputs.name.label',
                {phoneNumber: channel?.whatsappAccount?.phoneNumber}
              )}
            </Typography>
          }
          readOnly={!useHasPermission('edit', 'channels')}
          source="name"
        />
      </SimpleForm>
    </ExtendedEdit>
  )
}

const ChannelMerchantUsersShow: FC<ChannelProps> = ({channelId}) => {
  const styles = useStyles()
  return (
    <Show
      actions={false}
      className={styles.showRoot}
      id={channelId}
      resource="channels"
    >
      <SimpleShowLayout>
        <ReferenceManyToManyField
          addLabel={false}
          reference="merchant_users"
          sort={{field: "merchantUserId", order: 'DESC'}}
          through="channel_merchant_users"
          using="channelId,merchantUserId"
        >
          <Datagrid bulkActionButtons={false}>
            <TextField
              source="emailAddress"
              variant="body1"
            />
            <FunctionField
              label="Name"
              render={record => `${record.firstName} ${record.lastName}`}
              variant="body1"
            />
          </Datagrid>
        </ReferenceManyToManyField>
      </SimpleShowLayout>
    </Show>
  )
}

const CHANNEL_WHATSAPP_NUMBER_QUERY = gql`query($channelId: uuid!){
  channels_by_pk(id: $channelId){id whatsappAccount{phoneNumber}}
}`

const useStyles = makeStyles(theme => ({
  editRoot: {
    '& .MuiCardContent-root': {
      paddingBottom: theme.remSpacing(1),
    },
    '& > div': {
      marginTop: 0,
    },
    maxWidth: `${theme.typography.pxToRem(384)} !important`,
  },
  showRoot: {
    '& .MuiTableCell-sizeSmall': {
      maxWidth: theme.remSpacing(4),
      overflow: 'hidden',
      padding: theme.remSpacing(1),
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& > div ': {
      marginTop: 0,
    },
  },
}))

interface ChannelProps {
  channelId?: string
}

export {
  ChannelEdit,
  ChannelMerchantUsersShow,
}
